"use client";

import { useEffect, useState } from "react";
import { ArrowLeft, RotateCcw } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { WalkthroughStep } from "@/lib/utils/lomdaV3/types";
import { calloutClasses, resolveNextStepId } from "../../../lib/walkthrough";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";

export default function WalkthroughPlayer({
    steps,
    className,
}: {
    steps: WalkthroughStep[];
    className?: string;
}) {
    const locale = useLomdaLocale();
    const [currentId, setCurrentId] = useState(steps[0]?.id ?? "");
    const [history, setHistory] = useState<string[]>([]);

    useEffect(() => {
        setCurrentId(steps[0]?.id ?? "");
        setHistory([]);
    }, [steps]);

    if (steps.length === 0) {
        return (
            <div className={cn("lomda-v3-text-small flex items-center justify-center text-muted-foreground", className)}>
                {t("lomda-v3-editor.walkthrough.empty-play", locale)}
            </div>
        );
    }

    const index = Math.max(0, steps.findIndex((s) => s.id === currentId));
    const step = steps[index];
    const progress = ((index + 1) / steps.length) * 100;

    const go = (linkStepId: string) => {
        const next = resolveNextStepId(steps, step.id, linkStepId);
        if (!next) return;
        setHistory((h) => [...h, step.id]);
        setCurrentId(next);
    };

    const back = () => {
        setHistory((h) => {
            if (h.length === 0) return h;
            const prev = h[h.length - 1];
            setCurrentId(prev);
            return h.slice(0, -1);
        });
    };

    const restart = () => {
        setCurrentId(steps[0].id);
        setHistory([]);
    };

    return (
        <div className={cn(
                "lomda-v3-card-surface flex h-full w-full flex-col",
                className
            )}>
            <div className="flex items-center justify-between px-5 py-3">
                {history.length > 0 ? (
                    <button
                        type="button"
                        onClick={back}
                        className="lomda-v3-text-small flex items-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-muted-foreground transition hover:text-foreground"
                    >
                        <ArrowLeft className="size-4 rtl:rotate-180" />
                        {t("lomda-v3-editor.walkthrough.previous", locale)}
                    </button>
                ) : (
                    <span />
                )}
                <span className="lomda-v3-text-small text-muted-foreground">
                    {t("lomda-v3-editor.walkthrough.step-counter", locale, {
                        current: index + 1,
                        total: steps.length,
                    })}
                </span>
            </div>

            <div className="px-5">
                <div className="relative h-1 w-full overflow-hidden rounded-full bg-muted">
                    <div
                        className="absolute inset-y-0 start-0 rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            <div className="relative flex flex-1 items-center justify-center overflow-hidden p-5">
                <div
                    key={step.id}
                    className="w-full animate-in fade-in zoom-in-95 duration-300"
                >
                    {step.type === "info" ? (
                        <InfoView step={step} onGo={go} />
                    ) : (
                        <ImageView
                            step={step}
                            onGo={go}
                            isLast={index === steps.length - 1}
                        />
                    )}
                </div>

                {index === steps.length - 1 && (
                    <button
                        type="button"
                        onClick={restart}
                        className="lomda-v3-text-small absolute bottom-4 end-4 flex items-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-muted-foreground shadow-sm transition hover:text-foreground"
                    >
                        <RotateCcw className="size-3.5" />
                        {t("lomda-v3-editor.walkthrough.restart", locale)}
                    </button>
                )}
            </div>
        </div>
    );
}

function InfoView({
    step,
    onGo,
}: {
    step: Extract<WalkthroughStep, { type: "info" }>;
    onGo: (linkStepId: string) => void;
}) {
    const locale = useLomdaLocale();
    return (
        <div className="lomda-v3-card-surface mx-auto flex max-w-2xl flex-col items-center gap-6 rounded-2xl border p-10 text-center shadow-sm">
            {step.heading && (
                <div
                    className="lomda-v3-rich-text lomda-v3-text-h2 text-foreground"
                    dangerouslySetInnerHTML={{ __html: step.heading }}
                />
            )}
            {step.text && (
                <div
                    className="lomda-v3-rich-text text-muted-foreground"
                    dangerouslySetInnerHTML={{ __html: step.text }}
                />
            )}
            <div className="flex flex-wrap justify-center gap-3">
                {step.buttons.map((b) => (
                    <button
                        key={b.id}
                        type="button"
                        onClick={() => onGo(b.linkStepId)}
                        className="rounded-full bg-primary px-6 py-2.5 lomda-v3-text-label text-primary-foreground transition hover:opacity-90"
                    >
                        {b.text ||
                            t("lomda-v3-editor.walkthrough.next", locale)}
                    </button>
                ))}
                {step.buttons.length === 0 && (
                    <button
                        type="button"
                        onClick={() => onGo("")}
                        className="rounded-full bg-primary px-6 py-2.5 lomda-v3-text-label text-primary-foreground transition hover:opacity-90"
                    >
                        {t("lomda-v3-editor.walkthrough.next", locale)}
                    </button>
                )}
            </div>
        </div>
    );
}

function ImageView({
    step,
    onGo,
    isLast,
}: {
    step: Extract<WalkthroughStep, { type: "image" }>;
    onGo: (linkStepId: string) => void;
    isLast: boolean;
}) {
    const locale = useLomdaLocale();
    const interactive = step.interactive !== false;
    const aspect = step.aspect ?? "auto";
    const fit = step.fit ?? "cover";
    const aspectClass =
        aspect === "16:9"
            ? "aspect-video"
            : aspect === "4:3"
              ? "aspect-[4/3]"
              : aspect === "1:1"
                ? "aspect-square"
                : "";
    return (
        <div className="mx-auto flex max-w-4xl flex-col gap-4">
            {step.heading && (
                <div
                    className="lomda-v3-rich-text lomda-v3-text-h3 text-center"
                    dangerouslySetInnerHTML={{ __html: step.heading }}
                />
            )}
            <div
                className={cn(
                    "relative overflow-hidden rounded-xl border bg-muted shadow-sm",
                    aspectClass
                )}
            >
                {step.image ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        src={step.image}
                        alt={step.alt ?? ""}
                        className={cn(
                            aspect === "auto"
                                ? "w-full"
                                : cn(
                                      "absolute inset-0 h-full w-full",
                                      fit === "cover"
                                          ? "object-cover"
                                          : "object-contain"
                                  )
                        )}
                    />
                ) : (
                    <div className="lomda-v3-text-small flex aspect-video items-center justify-center text-muted-foreground">
                        {t("lomda-v3-editor.walkthrough.no-image", locale)}
                    </div>
                )}

                {step.hotspots.map((h) => {
                    const clickable = interactive && h.linkStepId !== "none";
                    return (
                    <button
                        key={h.id}
                        type="button"
                        disabled={!clickable}
                        onClick={
                            clickable ? () => onGo(h.linkStepId) : undefined
                        }
                        title={h.label}
                        style={{
                            left: `${h.x}%`,
                            top: `${h.y}%`,
                            width: `${h.width}%`,
                            height: `${h.height}%`,
                            borderColor: h.color,
                            boxShadow: `0 0 0 2px ${h.color}, 0 0 16px ${h.color}cc`,
                        }}
                        className={cn(
                            "absolute rounded-md border-2 transition",
                            clickable
                                ? "cursor-pointer hover:bg-primary/10"
                                : "cursor-default"
                        )}
                    >
                        <span
                            className="pointer-events-none absolute inset-0 animate-pulse rounded-md"
                            style={{ boxShadow: `0 0 0 3px ${h.color}55` }}
                        />
                        {h.label && h.textPosition !== "none" && (
                            <span
                                style={{ borderColor: h.color }}
                                className={cn(
                                    "lomda-v3-card-text absolute z-10 w-44 rounded-lg border bg-card p-2 text-start lomda-v3-text-small shadow-md",
                                    calloutClasses(h.textPosition)
                                )}
                            >
                                {h.label}
                            </span>
                        )}
                    </button>
                    );
                })}
            </div>

            {!interactive && !isLast && (
                <button
                    type="button"
                    onClick={() => onGo("")}
                    className="self-center rounded-full bg-primary px-6 py-2.5 lomda-v3-text-label text-primary-foreground transition hover:opacity-90"
                >
                    {t("lomda-v3-editor.walkthrough.next", locale)}
                </button>
            )}
        </div>
    );
}
